"use client";

import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { FC, ReactElement } from "react";
import { PopoverProps } from "react-tiny-popover";
import { SLOT_PRIORITY } from "../utils/publicationDistribution";
import Button from "./Button";
import SelectInput from "./SelectInput";
import Typography from "./Typography";

// Suggested posting hours shown in the header's per-slot time dropdowns:
// the in-house slot bands (SLOT_PRIORITY), listed chronologically.
const TIME_SLOT_SUGGESTIONS: number[] =
  [...SLOT_PRIORITY]
    .sort((a, b) => a.startHour - b.startHour)
    .map((slot) => slot.preferredHour);

export function formatSlotHourLabel(hour: number): string {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return format(date, "h:mm a");
}

type TimeSlotSelectProps = {
  /** Selected posting hour (0–23). */
  value: number;
  /** Default hour for this post's position in the selected daily cadence. */
  recommendedHour: number;
  onChange: (hour: number) => void;
  variant?: "tertiary" | "secondary";
  ariaLabel?: string;
  /** Replaces the default pill trigger (e.g. a full-width row inside a menu). */
  trigger?: ReactElement;
  positions?: PopoverProps["positions"];
  align?: PopoverProps["align"];
  /**
   * Hours held by sibling slots. Their suggestion rows are disabled; the
   * Custom… picker still commits them so the parent can resolve the
   * duplicate (swap).
   */
  takenHours?: ReadonlySet<number>;
};

/**
 * One posting-time dropdown: the in-house slot suggestions, with the
 * cadence's default for this post marked Recommended, plus a Custom…
 * row revealing the same time column as the
 * Starting-date picker. The engine works on hour slots, so custom picks
 * commit their hour.
 */
const TimeSlotSelect: FC<TimeSlotSelectProps> = ({
  value,
  recommendedHour,
  onChange,
  variant = "tertiary",
  ariaLabel,
  trigger,
  positions = ["bottom"],
  align = "center",
  takenHours,
}) => {
  const commit = (hour: number) => {
    if (hour !== value) onChange(hour);
  };

  return (
    <SelectInput
      value={value}
      onValueChange={commit}
      size="m"
      positions={positions}
      align={align}
      trigger={trigger ?? (
        <Button
          type="button"
          variant={variant}
          size="m"
          aria-label={ariaLabel ?? "Posting time"}
          data-tooltip-id="global"
          data-tooltip-content="Posting time"
        >
          <Button.Text>{formatSlotHourLabel(value)}</Button.Text>
          <ChevronDownIcon />
        </Button>
      )}
      options={TIME_SLOT_SUGGESTIONS.map((hour) => ({
        value: hour,
        // Never disable the current selection, even if state holds a duplicate.
        disabled: hour !== value && takenHours?.has(hour),
        label: (
          <Button.Text>
            {formatSlotHourLabel(hour)}
            {hour === recommendedHour ? (
              <>
                {" "}
                <Typography variant="overline" className="ml-1 text-slate-500 !font-normal">
                  Recommended
                </Typography>
              </>
            ) : null}
          </Button.Text>
        ),
      }))}
      contentFooter={({ close }) => (
        <SelectInput
          value={value}
          allowReselect
          onValueChange={(hour) => {
            close();
            commit(hour);
          }}
          size="m"
          variant="tertiary"
          className="w-full"
          buttonLabel="Custom…"
          showChevron={false}
          positions={["right", "left", "bottom"]}
          align="start"
          // Custom picks can use an occupied hour so the parent can swap slots.
          options={Array.from({ length: 24 }, (_, hour) => ({
            value: hour,
            label: formatSlotHourLabel(hour),
          }))}
        />
      )}
    />
  );
};

export default TimeSlotSelect;
